import { Download } from "lucide-react";
import React, { useEffect, useCallback } from "react";
import { ActionIcon, Tooltip } from "@mantine/core";
import dayjs from "dayjs";
import useExportSegmentHistoryApiService from "../services/api/ExportSegmentHistoryApiService";
import { useToasts } from "../../../providers/notifications/ToastsContext";
import { HttpClientError } from "../../../providers/HttpClient";
import SegmentHistoryEntry from "../services/model/SegmentHistoryEntry";

const CSV_HEADERS = ["Timestamp", "User", "Segment Name", "Action"];

const formatTimestamp = (timestamp: string): string =>
  dayjs(timestamp).format("YYYY-MM-DD HH:mm");

const escapeCSVField = (field: string): string => {
  if (field.includes(",") || field.includes("\n") || field.includes('"')) {
    return `"${field.replace(/"/g, '""')}"`;
  }
  return field;
};

const generateCSVContent = (entries: SegmentHistoryEntry[]): string => {
  const rows = [CSV_HEADERS.join(",")];

  entries.forEach((entry) => {
    const row = [
      escapeCSVField(formatTimestamp(entry.timestamp)),
      escapeCSVField(entry.actor_name),
      escapeCSVField(entry.segment_name),
      escapeCSVField(entry.action_label),
    ].join(",");
    rows.push(row);
  });

  return rows.join("\n");
};

const downloadCSV = (content: string, filename: string): void => {
  const blob = new Blob([content], { type: "text/csv;charset=utf-8;" });
  const url = window.URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  window.URL.revokeObjectURL(url);
};

const ExportSegmentHistoryButton: React.FC = () => {
  const { isLoading, isError, error, isSuccess, mutate, data, reset } =
    useExportSegmentHistoryApiService();
  const { showToast } = useToasts();

  useEffect(() => {
    if (isError) {
      showToast({
        strapiId: "segment_history_export_error",
        data: {
          message: HttpClientError(error),
        },
      });
      reset();
    }
  }, [isError, error, showToast, reset]);

  useEffect(() => {
    if (isSuccess && data?.results) {
      const csvContent = generateCSVContent(data.results);
      const filename = `segment-history-${dayjs().format("YYYY-MM-DD")}.csv`;
      downloadCSV(csvContent, filename);

      showToast({ strapiId: "segment_history_export_success" });
      reset();
    }
  }, [isSuccess, data, showToast, reset]);

  const handleExport = useCallback(() => {
    mutate();
  }, [mutate]);

  return (
    <Tooltip label={isLoading ? "Exporting..." : "Export history"}>
      <ActionIcon
        variant="outline"
        size="lg"
        onClick={handleExport}
        loading={isLoading}
        data-testid="export-segment-history-button"
      >
        <Download size={18} />
      </ActionIcon>
    </Tooltip>
  );
};

export default ExportSegmentHistoryButton;
